import React, { useState, useEffect, useRef, forwardRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import multiMonthPlugin from '@fullcalendar/multimonth';
import moment from 'moment';
import { Typography, Box, Modal, TextField, Checkbox, FormControlLabel, Button } from '@mui/material';
import Zoom from '@mui/material/Zoom';
import { useParams } from 'react-router-dom';
import { useService } from 'contexts/ServiceContextProvider';
import { useAuthorization } from 'contexts/AuthContextProvider';
import { useWorkspaces } from 'contexts/WorkspacesContextProvider';
import RefutalNotificationModal from './RefutalNotificationModal';
import RefuteRequestModal from './RefuteRequestModal';
import EditVacationModal from './EditVacationModal';
import { useTranslation } from "react-i18next";
import "../../css/components/Calendar/VacationCalendar.css";
import "../../css/main.css";
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VacationApprovalModal from 'components/Calendar/VacationApprovalModal'
import { calculateWeekDaysForVacation, extractCountryCode } from './calculateVacationDays.js'

import { DateTime } from 'luxon';
import ScrollAwareTooltip from 'components/Custom/ScrollAwareTooltip';

const ClickOrHoldButton = forwardRef(({ onClick, onHold, holdTime = 500, children, ...props }, ref) => {
    const timerRef = useRef(null);
    const isHeldRef = useRef(false);

    const handleMouseDown = () => {
        isHeldRef.current = false;
        timerRef.current = setTimeout(() => {
            isHeldRef.current = true;
            onHold();
        }, holdTime);
    };

    const handleMouseUp = () => {
        clearTimeout(timerRef.current);
        if (!isHeldRef.current) {
            onClick();
        }
    };

    const handleMouseLeave = () => {
        clearTimeout(timerRef.current);
    };

    const handleTouchStart = () => {
        isHeldRef.current = false;
        timerRef.current = setTimeout(() => {
            isHeldRef.current = true;
            onHold();
        }, holdTime);
    };

    const handleTouchEnd = () => {
        clearTimeout(timerRef.current);
        if (!isHeldRef.current) {
            onClick();
        }
    };

    return (
        <Button
            {...props}
            ref={ref}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            {children}
        </Button>
    );
});


const VacationCalendar = () => {
    const { t } = useTranslation();

    const [events, setEvents] = useState([]);
    const [showDeclined, setShowDeclined] = useState(false);
    const [tooltipContent, setTooltipContent] = useState({});
    const { workspaceId } = useParams();
    const { getLoggedUser } = useAuthorization();
    const { fetchWorkspaceVacations, UpdatePendingVacationRequest, getUserRemainingWorkspaceVacationDays, DeleteVacationRequest } = useService();
    const userColors = new Map();
    const [myUserPerms, setMyUserPerms] = useState(null);
    const calendarRef = useRef(null);

    const [modalOpenApproveVacations, setModalOpenApproveVacations] = useState(false);

    const handleOpenApproveVacationsModal = () => {
        setModalOpenApproveVacations(true);
    };

    const handleCloseApproveVacationsModal = () => {
        setModalOpenApproveVacations(false);
    };

    const [openModal, setOpenModal] = useState(false);
    const [currentEventId, setCurrentEventId] = useState(null);
    const [changeReason, setChangeReason] = useState('');

    const [openRefuteModal, setOpenRefuteModal] = useState(false);
    const [refutedVacations, setRefutedVacations] = useState([]);
    const [openNotificationModal, setOpenNotificationModal] = useState(false);

    const [openEditModal, setOpenEditModal] = useState(false);
    const [editVacationData, setEditVacationData] = useState(null);

    const [showMyVacationsOnly, setShowMyVacationsOnly] = useState(false);

    const workspaceContext = useWorkspaces();

    const handleOpenEditModal = (vacationData) => {
        setEditVacationData(vacationData);
        setOpenEditModal(true);
    };

    const handleCloseEditModal = () => {
        setOpenEditModal(false);
        setEditVacationData(null);
    };

    const handleOpenModal = (eventId) => {
        setCurrentEventId(eventId);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setChangeReason('');
    };

    const handleSuggestChanges = async () => {
        try {
            await UpdatePendingVacationRequest(workspaceId, [currentEventId], 'request_changes', changeReason);
            fetchVacations(workspaceId);
            handleCloseModal();
        } catch (error) {
            console.error('Error suggesting changes:', error);
        }
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (!workspaceContext.loading && workspaceContext.workspaceWorkers.length > 0) {
            const userPerms = workspaceContext.getLoggedUserPermissions();
            setMyUserPerms(userPerms);
        }
    }, [workspaceContext.loading, workspaceContext.workspaceWorkers]);

    const generateColor = (index) => {
        const hue = (index * 137.508) % 360;
        return `hsl(${hue}, 70%, 80%)`;
    };

    const getUserColor = (userId) => {
        if (!userColors.has(userId)) {
            const colorIndex = userColors.size;
            userColors.set(userId, generateColor(colorIndex));
        }
        return userColors.get(userId);
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (workspaceId && myUserPerms) {
            fetchVacations(workspaceId);
        }
    }, [workspaceId, myUserPerms, showDeclined, showMyVacationsOnly]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const trackerId = params.get("TrackerID");
        if (trackerId && events.length > 0) {
            const targetEvent = events.find(ev => ev.id === trackerId);
            if (targetEvent) {
                const calendarApi = calendarRef.current.getApi();
                calendarApi.gotoDate(targetEvent.start);

                params.delete("TrackerID");
                const queryString = params.toString();
                const newUrl = queryString
                    ? `${window.location.pathname}?${queryString}`
                    : window.location.pathname;
                window.history.replaceState(null, '', newUrl);
            }
        }
    }, [events]);

    const fetchVacations = async (workspaceId) => {
        try {
            const data = await fetchWorkspaceVacations(workspaceId);
            const vacationEvents = generateVacationEvents(data.vacations, data.holidays);
            setEvents(vacationEvents);
        } catch (error) {
            console.error('Error fetching vacation data:', error);
        }
    };

    const getBackgroundPattern = (type, userColor, isDeclined) => {
        if (isDeclined) {
            return `linear-gradient(45deg, rgba(255, 0, 0, 0.75), rgba(128, 0, 0, 0.75))`;
        }
        switch (type) {
            case 'RV':
                return `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 10 10'><path d='M0 0 L10 10 M-1 9 L9 -1 M1 11 L11 1' stroke='rgba(255, 255, 255, 0.5)' stroke-width='1' /></svg>"), linear-gradient(${userColor}, ${userColor})`;
            case 'SL':
                return `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'><path d='M0 0 L10 10 M10 0 L0 10' stroke='rgba(255, 255, 255, 0.5)' stroke-width='3' /></svg>"), linear-gradient(${userColor}, ${userColor})`;
            case 'PL':
                return `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='6' height='6' viewBox='0 0 6 6'><path d='M0 3 L6 3' stroke='rgba(255, 255, 255, 0.5)' stroke-width='4' /></svg>"), linear-gradient(${userColor}, ${userColor})`;
            case 'MPL':
                return `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'><rect x='1' y='1' width='6' height='6' fill='none' stroke='rgba(255, 255, 255, 0.5)' stroke-width='3' /></svg>"), linear-gradient(${userColor}, ${userColor})`;
            case 'PEN':
                return `url("data:image/svg+xml;utf8,\
                    <svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'>\
                        <text x='0' y='8' font-size='10' fill='rgba(255, 255, 255, 0.5)' font-weight='bold'>!</text>\
                    </svg>"), linear-gradient(#ff0000, #ff0000)`;
            default:
                return userColor;
        }
    };




    const generateVacationEvents = (vacationData, holidays) => {
        if (!myUserPerms) return [];



        const loggedInUserId = getLoggedUser();
        const refutedEvents = [];

        const events = vacationData
            .filter((vacation) => {
                if (vacation.type === 'REF') {
                    if (vacation.user.id === loggedInUserId) {
                        refutedEvents.push(vacation);
                    }
                    return false;
                }
                if (!showDeclined && vacation.declined) {
                    return false;
                }

                // Filter based on showMyVacationsOnly toggle
                if (showMyVacationsOnly) {
                    return vacation.user.id === loggedInUserId;
                }

                return vacation.visibility || myUserPerms.is_admin || myUserPerms.is_owner || vacation.user.id === loggedInUserId;
            })
            .map((vacation) => {
                const {
                    id,
                    title,
                    user,
                    date_start,
                    date_end,
                    requested_on,
                    approved_by,
                    approved_on,
                    visibility,
                    type,
                    declined,
                    description
                } = vacation;

                const userColor = getUserColor(user.id);
                let eventType = approved_on ? type : 'PEN';

                const vacationTypeMap = {
                    'RV': 'Regular Vacation',
                    'SL': 'Sick Leave',
                    'PL': 'Personal Leave',
                    'MPL': 'Maternity/Paternity Leave',
                    'PEN': 'Pending'
                };

                const overlappingHolidays = holidays.filter((holiday) => {
                    const holidayDate = DateTime.fromISO(holiday.date, { zone: 'utc' });
                    const startDate = DateTime.fromISO(date_start, { zone: 'utc' });
                    const endDate = DateTime.fromISO(date_end, { zone: 'utc' });

                    // Include holidays that match the user's country or are custom
                    const isRelevantHoliday =
                        holiday.country === extractCountryCode(user.location) || holiday.source === "Custom";

                    // Check if the holiday date is within the date range
                    const isWithinRange = holidayDate >= startDate && holidayDate <= endDate;

                    return isRelevantHoliday && isWithinRange;
                });


                const holidayOverlapContent = overlappingHolidays.length > 0
                    ? `<strong style="color:lightblue;">${t("calendar.holiday_overlap", { count: overlappingHolidays.length })}</strong><br/>
                    ${overlappingHolidays.map(holiday => `
                        - ${holiday.name} (${holiday.date}) 
                        ${holiday.source === "Custom"
                            ? `<span style="color:yellow; display: inline-flex; align-items: center;">
                                <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 0 20 20" width: "20px" height: "14px" fill="yellow" style="vertical-align: middle;">
                                    <path d="M21 4H7V2H5v20h2v-8h14l-2-5zm-6 5c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2"></path>
                                </svg>
                               </span>`
                            : `<img
                                src="https://flagcdn.com/w80/${extractCountryCode(user.location).toLowerCase()}.png"
                                alt="${user.location} Flag"
                                style="width: 20px; height: 14px; margin-left: 4px;"
                            />`
                        }
                    `).join('<br/>')}`
                    : '';



                const tooltipContent = `
                    <strong class="vacation-calendar-tooltip-title">${title}</strong><br/>
                    <strong>${t("calendar.vacation_user")}</strong> ${user.full_name}<br/>
                    <strong>${t("calendar.vacation_requested")}</strong> ${DateTime.fromISO(requested_on).toFormat('yyyy-MM-dd')}<br/>
                    ${approved_by ? `<strong>${t("calendar.vacation_approved_by")}</strong> ${approved_by.full_name}<br/>` : ''}
                    ${approved_on ? `<strong>${t("calendar.vacation_approved_on")}</strong> ${DateTime.fromISO(approved_on).toFormat('yyyy-MM-dd')}<br/>` : ''}
                    <strong>${t("global.visibility_title")}</strong> ${visibility ? t("global.public") : t("global.private")}<br/>
                    <strong>${t("calendar.vacation_type_title")}</strong> ${vacationTypeMap[type]}<br/>
        
                    ${holidayOverlapContent}
                    <br/>

                    ${description ? `
                        <strong>${t("calendar.event_description_title")}</strong>
                        <div class="vacation-calendar-tooltip-description">
                            ${description}
                        </div>` : ''
                    }

                    

                   
                `;


                // Adjust start and end date conversion for better timezone handling
                const startDate = DateTime.fromISO(date_start, { zone: 'utc' }).toISODate(); // Format as UTC date only
                const endDate = DateTime.fromISO(date_end, { zone: 'utc' }).plus({ days: 1 }).toISODate(); // Add a day to include the end date


                return {
                    id,
                    title: `${title} (${DateTime.fromISO(date_start).toFormat('MMM d')} - ${DateTime.fromISO(date_end).toFormat('MMM d, yyyy')})`,
                    start: startDate,
                    end: endDate,
                    textColor: '#000000',
                    allDay: true,


                    extendedProps: {
                        tooltipContent,
                        isApproved: !!approved_on,
                        isDeclined: !!declined,
                        user_id: user.id,
                        vacationDays: calculateWeekDaysForVacation(date_start, date_end, overlappingHolidays),
                        fullData: vacation,
                    },
                    backgroundColor: 'transparent',
                    borderColor: userColor,
                    backgroundPattern: getBackgroundPattern(eventType, userColor, declined),
                };
            });

        if (refutedEvents.length > 0) {
            setRefutedVacations(refutedEvents);
            setOpenNotificationModal(true);
        }

        return events;
    };

    const handleTooltipOpen = async (workspace_id, user_id, eventId, vacationDays, isApproved, isDeclined, do_not_deduct) => {
        if (isApproved || isDeclined) {
            setTooltipContent((prevContent) => ({
                ...prevContent,
                [eventId]: ''
            }));
            return;
        }

        const response = await getUserRemainingWorkspaceVacationDays(workspace_id, user_id);
        if (response && response.data) {
            const vacationInfo = response.data;
            const remainingAfterApproval = vacationInfo.total_available_days - vacationDays;
            const remainingTextColor = remainingAfterApproval < 0 ? 'tomato' : 'lightgreen';

            const remainingText = `<strong style="color: ${remainingTextColor};">${t("calendar.vacation_if_approved", { count: remainingAfterApproval })}</strong>`;

            setTooltipContent((prevContent) => ({
                ...prevContent,
                [eventId]: `
                <br>
                    <strong class="vacation-calendar-user-info">${t("calendar.vacation_user_info")}</strong><br/>
                    <strong>${t("calendar.vacation_per_year_part")}</strong> ${vacationInfo.remaining_vacation_days}<br/>
                    <strong>${t("calendar.vacation_rollovers_part")}</strong> ${vacationInfo.rollover_days}<br/>
                    <strong>${t("calendar.vacation_used_days_part")}</strong> ${vacationInfo.used_vacation_days}<br/>
                    <strong>${t("calendar.vacation_total_days_part")}</strong> ${vacationInfo.total_available_days}<br/>
                     ${!do_not_deduct ? remainingText :
                        `<span class="vacation-calendar-no-deduct-text">${t('calendar.vacation_no_deduct_request')}</span>`}
                `,
            }));
        } else {
            setTooltipContent((prevContent) => ({
                ...prevContent,
                [eventId]: 'Error fetching remaining vacation days',
            }));
        }
    };

    const handleApproveVacation = async (eventId) => {
        try {
            await UpdatePendingVacationRequest(workspaceId, [eventId], 'approve');
            fetchVacations(workspaceId);
        } catch (error) {
            console.error('Error approving vacation:', error);
        }

    };

    const handleDeclineVacation = async (eventId) => {
        try {
            await UpdatePendingVacationRequest(workspaceId, [eventId], 'decline');
            fetchVacations(workspaceId);
        } catch (error) {
            console.error('Error declining vacation:', error);
        }
    };

    const handleCancelVacation = async (eventId) => {
        try {
            await DeleteVacationRequest(workspaceId, [eventId], 'decline');
            fetchVacations(workspaceId);
        } catch (error) {
            console.error('Error declining vacation:', error);
        }
    };

    const renderEventContent = (eventInfo) => {
        const { start, end } = eventInfo.event;
        const calendarStart = moment(eventInfo.view.activeStart);
        const calendarEnd = moment(eventInfo.view.activeEnd);
        const extendedProps = eventInfo.event.extendedProps;

        const showLeftArrow = moment(start).isBefore(calendarStart, 'day');
        const showRightArrow = moment(end).isAfter(calendarEnd, 'day');

        const eventStyle = {
            backgroundImage: extendedProps.backgroundPattern,
            borderColor: eventInfo.event.borderColor,
            color: eventInfo.event.textColor,
        };

        const tooltipText = tooltipContent[eventInfo.event.id];

        const fullTooltipContent = myUserPerms?.is_admin || myUserPerms?.is_owner
            ? eventInfo.event.extendedProps.tooltipContent + tooltipText
            : eventInfo.event.extendedProps.tooltipContent;

        const do_not_deduct = eventInfo.event.extendedProps.fullData.do_not_deduct;


        const remainingAfterApproval = tooltipContent[eventInfo.event.id]?.match(/If approved this user will have (-?\d+) days remaining./);
        const remainingDays = remainingAfterApproval ? parseInt(remainingAfterApproval[1], 10) : null;

        return (
            <ScrollAwareTooltip
                slots={{
                    transition: Zoom,
                }}
                title={
                    <div>
                        <div dangerouslySetInnerHTML={{ __html: fullTooltipContent }} />
                        {(extendedProps.user_id === getLoggedUser() || myUserPerms.is_admin || myUserPerms.is_owner) && (
                            <Box>
                                {!extendedProps.isApproved && !extendedProps.isDeclined && (
                                    <>
                                        <Box className="vacation-calendar-actions-box">
                                            {/* If it's the logged-in user's own vacation */}
                                            {extendedProps.user_id === getLoggedUser() && !myUserPerms.is_admin && !myUserPerms.is_owner && (
                                                <>
                                                    <Button
                                                        variant="contained"
                                                        className="vacation-calendar-edit"
                                                        onClick={() => handleOpenEditModal(eventInfo.event.extendedProps.fullData)}
                                                    >
                                                        {t("calendar.event_edit")}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        className="vacation-calendar-decline"
                                                        onClick={() => handleCancelVacation(eventInfo.event.id)}
                                                    >
                                                        {t("global.cancel")}
                                                    </Button>
                                                </>
                                            )}

                                            {/* If it's the admin/owner and it's someone else's vacation */}
                                            {(myUserPerms.is_admin || myUserPerms.is_owner) && extendedProps.user_id !== getLoggedUser() && (
                                                <>
                                                    <ScrollAwareTooltip title="Hold to Open Approval View">
                                                        <ClickOrHoldButton
                                                            variant="contained"
                                                            className={`vacation-calendar-approve ${remainingDays < 0 && !do_not_deduct ? 'vacation-calendar-approve-disabled' : 'vacation-calendar-approve-active'}`}
                                                            onClick={() => handleApproveVacation(eventInfo.event.id)}
                                                            onHold={() => handleOpenApproveVacationsModal()}
                                                            disabled={remainingDays < 0 && !do_not_deduct}
                                                        >
                                                            {t("calendar.vacation_approve_button")}
                                                        </ClickOrHoldButton>
                                                    </ScrollAwareTooltip>
                                                    <Button
                                                        variant="contained"
                                                        className="vacation-calendar-refute"
                                                        onClick={() => handleOpenModal(eventInfo.event.id)}
                                                    >
                                                        {t("calendar.vacation_refute_button")}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        className="vacation-calendar-decline"
                                                        onClick={() => handleDeclineVacation(eventInfo.event.id)}
                                                    >
                                                        {t("calendar.vacation_decline_button")}
                                                    </Button>
                                                </>
                                            )}

                                            {/* If it's the admin/owner's own vacation */}
                                            {(myUserPerms.is_admin || myUserPerms.is_owner) && extendedProps.user_id === getLoggedUser() && (
                                                <>
                                                    <ScrollAwareTooltip title="Hold to Open Approval View">
                                                        <ClickOrHoldButton
                                                            variant="contained"
                                                            className={`vacation-calendar-approve ${remainingDays < 0 && !do_not_deduct ? 'vacation-calendar-approve-disabled' : 'vacation-calendar-approve-active'}`}
                                                            onClick={() => handleApproveVacation(eventInfo.event.id)}
                                                            onHold={() => handleOpenApproveVacationsModal()}
                                                            disabled={remainingDays < 0 && !do_not_deduct}
                                                        >
                                                            {t("calendar.vacation_approve_button")}
                                                        </ClickOrHoldButton>
                                                    </ScrollAwareTooltip>

                                                    <Button
                                                        variant="contained"
                                                        className="vacation-calendar-edit"
                                                        onClick={() => handleOpenEditModal(eventInfo.event.extendedProps.fullData)}
                                                    >
                                                        {t("calendar.event_edit")}
                                                    </Button>

                                                    <Button
                                                        variant="contained"
                                                        className="vacation-calendar-decline"
                                                        onClick={() => handleCancelVacation(eventInfo.event.id)}
                                                    >
                                                        {t("global.cancel")}
                                                    </Button>
                                                </>
                                            )}
                                        </Box>
                                    </>
                                )}
                            </Box>
                        )}


                    </div>
                }
                arrow
                onOpen={() => handleTooltipOpen(workspaceId, eventInfo.event.extendedProps.user_id, eventInfo.event.id, extendedProps.vacationDays, extendedProps.isApproved, extendedProps.isDeclined, extendedProps.fullData.do_not_deduct)}
            >
                <div className="vacation-calendar-event-style"
                    style={eventStyle}>
                    {showLeftArrow && (
                        <span className="vacation-calendar-left-arrow">
                            &#9664;
                        </span>
                    )}
                    <div className="vacation-calendar-event-title">
                        <i>{eventInfo.event.title}</i>
                    </div>
                    {showRightArrow && (
                        <span className="vacation-calendar-right-arrow">
                            &#9654;
                        </span>
                    )}
                </div>
            </ScrollAwareTooltip>
        );
    };


    return (
        <div>
            <Typography
                variant="h4"
                className="vacation-calendar-component"
            >
                {t("calendar.vacation_calendar")}
            </Typography>
            <Box className="vacation-calendar-checkbox">
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showMyVacationsOnly}
                            onChange={() => setShowMyVacationsOnly(!showMyVacationsOnly)}
                            color="primary"
                        />
                    }
                    label={t("calendar.vacation_only_mine")}
                />
            </Box>
            <div className="vacation-calendar-fc">
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin, multiMonthPlugin]}
                    initialView="dayGridMonth"
                    events={events}
                    eventTimeZone="UTC"
                    multiMonthMinWidth={600}
                    multiMonthMaxColumns={2}
                    eventContent={renderEventContent}
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: myUserPerms?.is_admin || myUserPerms?.is_owner
                            ? 'dayGridMonth,multiMonthYear,customButton'
                            : 'dayGridMonth,multiMonthYear',
                    }}
                    customButtons={{
                        customButton: {
                            text: t("calendar.vacation_show_declined"),
                            click: () => setShowDeclined((prev) => !prev),
                        },
                    }}
                    weekends={true}
                    editable={false}
                    selectable={false}
                    eventDisplay="block"
                    height="auto"
                    showNonCurrentDates={false}
                    fixedWeekCount={false}
                    eventTimeFormat={{
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                        meridiem: false,
                    }}
                    dayMaxEventRows={false}
                    dayMaxEvents={false}
                    locale={t("global.lng")}
                    buttonText={{
                        today: t("calendar.today"),
                        month: t("calendar.month"),
                        year: t("calendar.year")
                    }}
                />
            </div>

            <Modal open={openModal} onClose={handleCloseModal}>
                <Box className="vacation-calendar-refute-modal">
                    <Typography variant="h6" className="vacation-calendar-refute-title">{t("calendar.vacation_refute")}</Typography>
                    <TextField
                        label={t("calendar.vacation_refute_reason")}
                        multiline
                        fullWidth
                        value={changeReason}
                        onChange={(e) => setChangeReason(e.target.value)}
                        rows={4}
                        variant="outlined"
                        InputLabelProps={{
                            className: "vacation-calendar-input-title-color"
                        }}
                        InputProps={{
                            className: "vacation-calendar-input-color"
                        }}
                        className="vacation-calendar-refute-input"
                    />
                    <Box className="buttons-flex vacation-calendar-refute-buttons">
                        <Button
                            variant="outlined"
                            className="decline-button"
                            onClick={handleCloseModal}
                        >
                            <CancelIcon style={{ marginRight: 4 }} />
                            {t("global.cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            className="accept-button"
                            onClick={handleSuggestChanges}
                        >
                            <CheckCircleIcon style={{ marginRight: 4 }} />
                            {t("calendar.vacation_submit")}
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <RefutalNotificationModal
                open={openNotificationModal}
                handleAcknowledge={() => {
                    setOpenNotificationModal(false);
                    setOpenRefuteModal(true);
                }}
            />
            <RefuteRequestModal
                open={openRefuteModal}
                handleClose={() => setOpenRefuteModal(false)}
                refutedVacations={refutedVacations}
            />
            <EditVacationModal
                open={openEditModal}
                handleClose={handleCloseEditModal}
                vacationData={editVacationData}
                workspaceId={workspaceId}
                handleSubmitEdit={(updatedData) => {
                    UpdatePendingVacationRequest(workspaceId, [editVacationData.id], 'edit', '', updatedData);
                    fetchVacations(workspaceId);
                    handleCloseEditModal();
                }}
            />
            {modalOpenApproveVacations && (
                <VacationApprovalModal
                    open={modalOpenApproveVacations}
                    handleCloseApproveVacationsModal={handleCloseApproveVacationsModal}
                    workspace_id={workspaceId}
                />
            )}
        </div>
    );
};

export default VacationCalendar;
